import { observer } from "mobx-react-lite";
import { useCallback, useState } from "react";
import { useParams } from "react-router-dom";

import { usePromptVersionLogsDateRangeConfig } from "../hooks/usePromptVersionLogsDateRangeConfig";

import EntityVersionAnalyticsSheet, {
  OpenAnalyticsButton,
  snapshotStructuredSearchQuery,
  type EntityVersionAnalyticsQuerySnapshot,
} from "@/components/EntityVersionAnalyticsSheet";
import LoadingSpinner from "@/components/LoadingSpinner";
import { StatsShimmer } from "@/components/shared/StatsShimmer";
import { useStructuredSearchStore } from "@/components/StructuredSearch";
import { useAuth } from "@/context/auth-context";
import { TimezoneProvider } from "@/context/timezone-context";
import { useUser } from "@/context/user-context";
import { useStatBoxes } from "@/hooks/useStatsBoxes";
import RequestLogsAgGrid from "@/routes/analytics/RequestLogsAgGrid";
import { usePromptVersion } from "@/queries";
import { useRequestStats } from "@/queries/prompts/useRequestStats";

const PromptTemplateAnalyticsContent = observer(() => {
  const { promptId, versionId } = useParams();
  const userToken = useAuth()?.userToken;
  const { activeWorkspace } = useUser();
  const structuredSearchStore = useStructuredSearchStore();
  const [isAnalyticsSheetOpen, setIsAnalyticsSheetOpen] = useState(false);
  const [analyticsQuerySnapshot, setAnalyticsQuerySnapshot] =
    useState<EntityVersionAnalyticsQuerySnapshot | null>(null);
  const [analyticsSheetKey, setAnalyticsSheetKey] = useState(0);

  // Version-wide totals — independent of the table's date range / filters.
  const statsQuery = useRequestStats(promptId!, versionId || "");
  const statsBoxes = useStatBoxes(statsQuery.data?.stats);
  const isLoadingStats = statsQuery.isLoading || statsQuery.isFetching || !statsQuery.data?.success;
  const statsComponent = isLoadingStats ? <StatsShimmer /> : statsBoxes;

  const promptVersion = usePromptVersion(
    userToken || "",
    Number(promptId),
    versionId ? Number(versionId) : null,
  );

  const versionDateRangeConfig = usePromptVersionLogsDateRangeConfig({
    promptVersion: promptVersion.data,
  });

  const isLoadingRequestsContext = !versionDateRangeConfig || promptVersion.isLoading;

  const handleOpenAnalytics = useCallback(() => {
    setAnalyticsQuerySnapshot(snapshotStructuredSearchQuery(structuredSearchStore));
    setAnalyticsSheetKey(key => key + 1);
    setIsAnalyticsSheetOpen(true);
  }, [structuredSearchStore]);

  return (
    <div className="flex h-full flex-col gap-y-2 overflow-hidden pt-2">
      {statsComponent}
      <div className="min-h-0 flex-1 overflow-hidden">
        {!isLoadingRequestsContext && versionDateRangeConfig ? (
          <RequestLogsAgGrid
            syncUrl
            useExternalStructuredSearchProvider
            searchbarHeaderActions={
              <OpenAnalyticsButton
                onClick={handleOpenAnalytics}
                cutoverDate={activeWorkspace?.search_cutover_date}
              />
            }
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            <LoadingSpinner />
          </div>
        )}
      </div>
      <EntityVersionAnalyticsSheet
        open={isAnalyticsSheetOpen}
        onOpenChange={setIsAnalyticsSheetOpen}
        querySnapshot={analyticsQuerySnapshot}
        variant="requests"
        contentKey={analyticsSheetKey}
      />
    </div>
  );
});

const PromptTemplateAnalytics = () => (
  <TimezoneProvider>
    <PromptTemplateAnalyticsContent />
  </TimezoneProvider>
);

export default PromptTemplateAnalytics;
